/**
 * SEOEditor Component
 *
 * Editor for SEO settings in web placements.
 * Controls page title, meta description, and Open Graph image.
 *
 * No enable toggle - if values are provided, they're used.
 *
 * @module webplacements/components/editors
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { WebSEOConfig } from '@modules/webplacements/types/webplacements.types';

export interface SEOEditorProps {
  /** Current SEO config */
  value: WebSEOConfig | undefined;
  /** Called when config changes */
  onChange: (config: WebSEOConfig | undefined) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

/**
 * SEOEditor Component
 *
 * No enable toggle - SEO is active when any field has value.
 */
export const SEOEditor: React.FC<SEOEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();

  const handleFieldChange = (field: keyof WebSEOConfig) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = event.target.value;
    const updatedConfig = {
      ...value,
      [field]: newValue,
    };

    // If all fields are empty, clear the config entirely
    if (!updatedConfig.title && !updatedConfig.description && !updatedConfig.ogImage) {
      onChange(undefined);
    } else {
      onChange(updatedConfig);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary">
          SEO & Social Sharing
        </Typography>
        <Tooltip title="Customize page title, description, and social sharing image for better visibility">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Page Title */}
        <TextField
          label="Page Title"
          value={value?.title ?? ''}
          onChange={handleFieldChange('title')}
          disabled={disabled}
          fullWidth
          size="small"
          helperText="Shows in browser tab and search results (recommended: 50-60 characters)"
          placeholder="My Amazing Funnel"
        />

        {/* Meta Description */}
        <TextField
          label="Meta Description"
          value={value?.description ?? ''}
          onChange={handleFieldChange('description')}
          disabled={disabled}
          fullWidth
          size="small"
          multiline
          rows={2}
          helperText="Description for search engines and social sharing (recommended: 150-160 characters)"
          placeholder="Discover how our solution can help you achieve your goals..."
        />

        {/* Open Graph Image */}
        <TextField
          label="Social Share Image (OG Image)"
          value={value?.ogImage ?? ''}
          onChange={handleFieldChange('ogImage')}
          disabled={disabled}
          fullWidth
          size="small"
          helperText="Image shown when shared on social media (recommended: 1200x630px)"
          placeholder="https://example.com/og-image.jpg"
        />
      </Box>

      {/* Info Box */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: theme.palette.info.main + '10',
          borderRadius: 1,
          border: `1px solid ${theme.palette.info.main}30`,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          SEO settings are injected into the HTML when the funnel is published.
          Changes require republishing to take effect.
        </Typography>
      </Box>
    </Box>
  );
};

export default SEOEditor;
